import React from "react";
import { Whisper, Tooltip } from "rsuite";

import {
    renderMoney, copyArrayWithoutReference,
    formatDateApi, getRangeByNumber
} from "utils";

const TO_DATE_COLUMN = {
    label: "To Date",
    dataKey: "end_date",
    formatData: "date",
    displayWhen: ["trunk_number"],
    sortable: true,
    align: "right",
    minWidth: 100,
};

const ACCOUNT_BASE_COLUMNS = [
    {
        label: "Number",
        dataKey: "number",
        sortable: true,
        value: ({ number, numbers_count }) => getRangeByNumber(number, numbers_count),
        displayWhen: ["trunk_number"],
        flexGrow: 1,
        minWidth: 196,
        minWidthMobile: 225,
    },
    {
        label: "Range",
        dataKey: "name",
        sortable: true,
        displayWhen: ["trunk_number", "price_range"],
        flexGrow: 2,
        minWidth: 196,
        minWidthMobile: 300,
    },
    {
        label: "Subdestination",
        dataKey: "subdestination_name",
        sortable: true,
        displayWhen: ["subdestination"],
        flexGrow: 2,
        minWidth: 196,
        minWidthMobile: 300,
    },
    {
        label: "Rate",
        dataKey: "rate",
        sortable: true,
        value: ({ rate }) => renderMoney(rate),
        displayWhen: ["trunk_number"],
        width: 100,
        align: "right"
    },
    {
        label: "Rate",
        dataKey: "rate",
        sortable: true,
        value: ({ rate }) => renderMoney(rate),
        displayWhen: ["price_range", "subdestination"],
        flexGrow: 1,
        align: "right"
    },
];

const ACCOUNT_SERVICE_COLUMNS = [
    {
        label: "Billing increment",
        dataKey: "rrc_name",
        displayWhen: ["trunk_number"],
        width: 150,
        align: "right"
    },
    {
        label: "IVR",
        dataKey: "ivr_name",
        displayWhen: ["trunk_number"],
        value: ({ ivr_name }) => ivr_name,
        flexGrow: 1,
        minWidth: 140,
        minWidthMobile: 200,
    },
];

const ACCOUNT_TRAILING_COLUMNS = [
    {
        label: "User",
        dataKey: "modify_user_name",
        displayWhen: ["trunk_number"],
        flexGrow: 1,
        minWidth: 196,
        minWidthMobile: 300,
    },
    {
        label: "From Date",
        dataKey: "start_date",
        displayWhen: ["trunk_number"],
        formatData: "datetime_local",
        sortable: true,
        align: "right",
        width: 190,
        minWidth: 160,
    },
    {
        label: "Modified at",
        dataKey: "modified_at",
        displayWhen: ["trunk_number"],
        formatData: "datetime_local",
        sortable: true,
        align: "right",
        width: 190,
        minWidth: 160,
    },
    {
        label: "Allocated numbers",
        dataKey: "trunk_numbers",
        sortable: true,
        displayWhen: ["price_range"],
        align: "right",
        flexGrow: 1,
        minWidth: 196,
    },
    {
        label: "Allocated numbers and ranges",
        dataKey: "allocated_numbers_and_ranges",
        displayWhen: ["subdestination"],
        align: "right",
        flexGrow: 1,
        minWidth: 196,
    },
];

const insertColumnBeforeDataKey = (columns, column, beforeDataKey) => {
    const targetIndex = columns.findIndex((item) => item.dataKey === beforeDataKey);
    if (targetIndex < 0) {
        return [...columns, column];
    }

    return [
        ...columns.slice(0, targetIndex),
        column,
        ...columns.slice(targetIndex),
    ];
};

const renderGanTestNumbers = ({ test_number_list }) => {
    const data = test_number_list && test_number_list.join(", ");
    return (
        <Whisper
            enterable
            placement="bottomStart"
            speaker={<Tooltip>{data}</Tooltip>}
        >
            <span>{data}</span>
        </Whisper>
    );
};

const renderGanPayoutForDialers = ({ rate_list = [] }) => {
    const payoutGroups = rate_list.reduce((summ, item) => {
        return {
            ...summ,
            [item.cur_name]: summ[item.cur_name]
                ? summ[item.cur_name] + `, ${item.payment_terms_name}: ${renderMoney(item.rate, item.cur_name)}`
                : `${item.payment_terms_name}: ${renderMoney(item.rate, item.cur_name)}`
        };
    }, {});

    return (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: -4 }}>
            <div style={{ fontSize: 12, lineHeight: "1.2em", flexShrink: 0, flexGrow: 1 }}>
                {Object.keys(payoutGroups).map((item) => <span key={item}>{payoutGroups[item]}<br /></span>)}
            </div>
        </div>
    );
};

const renderGanAllocatedTo = ({ test_number, allocated_account_name, allocated_rate, allocated_cur_name, blocked }) => {
    const isAllocated = !!allocated_account_name;
    let allocatedText = "not allocated";

    if (blocked) {
        allocatedText = "blocked";
    } else if (test_number) {
        allocatedText = allocated_account_name;
    } else if (isAllocated) {
        allocatedText = `${allocated_account_name} ${allocated_rate || 0} ${allocated_cur_name}`;
    }

    return allocatedText;
};

const createGanLimitsRenderer = (SecondStagedCell) => {
    return ({ trafic_limit_list }) => {
        const sortedList = copyArrayWithoutReference(trafic_limit_list).sort((a, b) => {
            if (a.senderid < b.senderid) return -1;
            if (a.senderid >= b.senderid) return 1;
            return 0;
        });

        const firstElement = sortedList.length ? sortedList[0] : {};
        const remainingElementsList = sortedList.splice(1, sortedList.length);

        const stringElementsList = remainingElementsList.map((value) => {
            const limitDay = value.day ? `${value.day} SMS per day` : "";
            const limitHour = value.hour ? `${value.hour} SMS per hour` : "";
            const bothLimitsSeparator = value.day && value.hour ? "/" : "";
            const hourDayValue = `${limitDay}${bothLimitsSeparator}${limitHour}`;

            if (hourDayValue) {
                return `${value?.senderid || "<empty>"} - ${hourDayValue}`;
            }

            return "No limits";
        });

        const newLinedString = stringElementsList.length
            ? stringElementsList.map((element, idx) => <p key={`limit-${idx}`}>{element}</p>)
            : "No more limits";

        const hour = firstElement.hour;
        const day = firstElement.day;
        const limitDay = day ? `${day} SMS per day` : "";
        const limitHour = hour ? `${hour} SMS per hour` : "";
        const bothLimitsSeparator = day && hour ? "/" : "";

        const finalValue = limitDay && limitHour ? <>
            <>{firstElement.senderid || "Any senderId"}</> - <SecondStagedCell>
                <span className="first">{limitDay}</span>
                <span className="second">{limitHour}</span>
            </SecondStagedCell>
        </> : "No limits";

        const hourDayValue = `${limitDay}${bothLimitsSeparator}${limitHour}`;
        const finalValueTooltip = hourDayValue
            ? `${firstElement.senderid || "Any senderId"} - ${hourDayValue}${stringElementsList.length ? "..." : ""}`
            : "No limits";

        return (
            <Whisper
                enterable
                placement="auto"
                speaker={<Tooltip>{hourDayValue && !stringElementsList.length ? finalValueTooltip : newLinedString}</Tooltip>}
            >
                <span style={{ fontSize: 13, display: "flex" }}>{finalValue}</span>
            </Whisper>
        );
    };
};

export const buildAccountColumns = ({ service, showToDate }) => {
    const withServiceColumns = service
        ? [...ACCOUNT_BASE_COLUMNS, ...ACCOUNT_SERVICE_COLUMNS, ...ACCOUNT_TRAILING_COLUMNS]
        : [...ACCOUNT_BASE_COLUMNS, ...ACCOUNT_TRAILING_COLUMNS];

    if (!showToDate) {
        return withServiceColumns;
    }

    return insertColumnBeforeDataKey(withServiceColumns, TO_DATE_COLUMN, "modified_at");
};

export const buildGanColumns = ({ SecondStagedCell }) => {
    const renderGanLimits = createGanLimitsRenderer(SecondStagedCell);

    return [
        {
            label: "Number",
            dataKey: "number",
            displayWhen: ["numbers"],
            sortable: true,
            minWidth: 160,
            minWidthMobile: 180,
        },
        {
            label: "Group name",
            dataKey: "gan_group_name",
            displayWhen: ["numbers"],
            sortable: true,
            flexGrow: 1,
            minWidth: 196,
            minWidthMobile: 225,
        },
        {
            label: "Group name",
            dataKey: "name",
            displayWhen: ["groups"],
            sortable: true,
            minWidth: 196,
            minWidthMobile: 225,
        },
        {
            label: "Subdestination (mcc mnc)",
            dataKey: "subdestination_name",
            displayWhen: ["numbers"],
            sortable: true,
            value: ({ subdestination_name, mcc, mnc }) => `${subdestination_name} (${mcc}${mnc})`,
            flexGrow: 1,
            minWidth: 200,
            minWidthMobile: 220,
        },
        {
            label: "Test numbers",
            dataKey: "test_number_list",
            displayWhen: ["groups"],
            value: renderGanTestNumbers,
            flexGrow: 1,
            minWidth: 196,
            minWidthMobile: 225,
        },
        {
            label: "Allocated numbers",
            dataKey: "allocated_numbers",
            displayWhen: ["groups"],
            value: ({ allocated_numbers, all_numbers }) => `${allocated_numbers}/${all_numbers}`,
            flexGrow: 1,
            minWidth: 120,
            minWidthMobile: 140,
        },
        {
            label: "Payout",
            dataKey: "supplier_rate",
            displayWhen: ["numbers", "groups"],
            sortable: true,
            value: ({ supplier_rate, supplier_cur_name }) => renderMoney(supplier_rate, supplier_cur_name),
            minWidth: 110,
            minWidthMobile: 120,
        },
        {
            label: "Payout for dialers",
            dataKey: "rate_list",
            displayWhen: ["groups"],
            value: renderGanPayoutForDialers,
            flexGrow: 1,
            minWidth: 120,
            minWidthMobile: 140,
        },
        {
            label: "Allocated to",
            dataKey: "payout",
            displayWhen: ["numbers"],
            value: renderGanAllocatedTo,
            width: 220,
            minWidth: 120,
            minWidthMobile: 140,
        },
        {
            label: "Modified by",
            dataKey: "modify_user",
            displayWhen: ["numbers"],
            flexGrow: 1,
            minWidth: 250
        },
        {
            label: "Modify date",
            dataKey: "modify_date",
            displayWhen: ["numbers"],
            formatData: "datetime_local",
            minWidth: 180
        },
        {
            label: "Limits",
            dataKey: "trafic_limit_list",
            displayWhen: ["numbers", "groups"],
            value: renderGanLimits,
            flexGrow: 1,
            minWidth: 230,
            minWidthMobile: 380
        }
    ];
};

export const buildTransitColumns = () => {
    return [
        {
            label: "Prefix",
            dataKey: "prefix",
            sortable: true,
            minWidth: 160,
            minWidthMobile: 180,
        },
        {
            label: "Name (mcc_mnc)",
            dataKey: "name",
            value: ({ name, mcc, mnc }) => `${name || ""} (${mcc}${mnc})`,
            sortable: true,
            flexGrow: 2,
            minWidth: 196,
            minWidthMobile: 225,
        },
        {
            label: "Payout",
            dataKey: "rate",
            sortable: true,
            value: ({ rate, currency_name }) => renderMoney(rate, currency_name),
            minWidth: 110,
            minWidthMobile: 120,
        },
        {
            label: "Date",
            dataKey: "start_date",
            sortable: true,
            value: ({ start_date, end_date }) => `${formatDateApi(start_date || "")}${end_date ? ` - ${formatDateApi(end_date || "")}` : ""}`,
            flexGrow: 1,
            minWidth: 120,
            minWidthMobile: 140,
        },
        {
            label: "Created at",
            dataKey: "created_at",
            sortable: true,
            formatData: "datetime_local",
            minWidth: 180,
            minWidthMobile: 200,
        },
        {
            label: "Modified by",
            dataKey: "modify_user",
            flexGrow: 1,
            minWidth: 250
        },
        {
            label: "Modify date",
            dataKey: "modify_date",
            formatData: "datetime_local",
            minWidth: 180
        }
    ];
};

export const filterColumnsByDisplay = (columns, currentDisplay) => {
    if (!columns || !columns.length) {
        return [];
    }

    return columns.filter((column) => {
        return "displayWhen" in column ? column.displayWhen.includes(currentDisplay) : true;
    });
};

export const buildColumnsByMode = ({ service, showToDate, SecondStagedCell }) => {
    return {
        account: buildAccountColumns({ service, showToDate }),
        gan: buildGanColumns({ SecondStagedCell }),
        transit: buildTransitColumns(),
    };
};
